import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import type { RecommendationItem } from 'voyager';
import { BasketDrawerTemplate } from 'voyager';
import type { BasketDrawerView } from 'voyager';
import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { ReduxPageType } from '@common-types/ReduxPage';
import { useLabels } from '@features/Menu/useLabels';
import { getItineraryItems } from '@utils/getItineraryView';
import { calculateTotalNumberOfItemsInBasket, mapModules } from '../BasketDrawer.helpers';
import { ModuleType } from '../BasketListing.types';
import { useModulesToRecommend } from '../hooks/useModulesToRecommend';
import { useStartCheckout } from '../hooks/useStartCheckout';
import { toBasketCardItems } from './adapters/toBasketCardItem';
import { toItineraryGuests } from './adapters/toItineraryGuests';

type BasketDrawerV2ConnectedProps = {
  open: boolean;
  onClose: () => void;
  propertyModules: string[];
};

export const BasketDrawerV2Connected: FC<BasketDrawerV2ConnectedProps> = ({
  open,
  onClose,
  propertyModules,
}) => {
  const { t } = useTranslations();
  const labels = useLabels();
  const router = useRouter();
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as any;
  const spaPage = useSelector(entitySelectors.getSpaPage) as ReduxPageType;
  const tablesPage = useSelector(entitySelectors.getTablesPage) as ReduxPageType;
  const roomsPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'rooms')
  ) as ReduxPageType;
  const [view, setView] = useState<BasketDrawerView>('itemised');
  const { startCheckout } = useStartCheckout();

  const guestItineraries = getItineraryItems(basketDetails?.items ?? [], labels);

  const items = useMemo(
    () =>
      toBasketCardItems(basketDetails?.items ?? [], {
        t,
        currencyCode: basketDetails?.total?.currencyCode ?? '',
        moduleImages: {
          rooms: roomsPage?.heroBackgroundImage?.url,
          spa: spaPage?.heroBackgroundImage?.url,
          tables: tablesPage?.heroBackgroundImage?.url,
        },
      }),
    [
      basketDetails?.items,
      basketDetails?.total?.currencyCode,
      t,
      roomsPage?.heroBackgroundImage?.url,
      spaPage?.heroBackgroundImage?.url,
      tablesPage?.heroBackgroundImage?.url,
    ]
  );

  const guests = useMemo(() => toItineraryGuests(guestItineraries), [guestItineraries]);

  const recommendedModules = useModulesToRecommend({
    modules: propertyModules as ModuleType[],
    items: basketDetails?.items ?? [],
  });

  const { recommendations, routeById } = useMemo(() => {
    const pages = mapModules(recommendedModules, { spaPage, tablesPage, roomsPage }).filter(
      (page): page is NonNullable<typeof page> => page != null
    );
    const recs: RecommendationItem[] = pages.map((page) => ({
      id: String(page.id),
      name: page.title,
      imageUrl: page.heroBackgroundImage?.url,
    }));
    const map = new Map<string, string>(pages.map((page) => [String(page.id), page.route]));
    return { recommendations: recs, routeById: map };
  }, [recommendedModules, spaPage, tablesPage, roomsPage]);

  if (!open || !basketDetails) return null;

  return (
    <BasketDrawerTemplate
      open={open}
      onClose={onClose}
      items={items}
      totalPrice={{
        amount: basketDetails.total.amount,
        currencyCode: basketDetails.total.currencyCode,
      }}
      itemCount={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
      view={view}
      onViewChange={setView}
      guests={guests}
      recommendations={recommendations}
      onRecommendationSelect={(id) => {
        const route = routeById.get(id);
        if (route) router.push({ pathname: route });
      }}
      onViewBasket={() => router.push({ pathname: Routes.BASKET })}
      onCheckout={startCheckout}
      onContinueShopping={onClose}
      labels={{
        title: t('basketDrawer.title'),
        itemisedView: t('basket.itemisedView'),
        itineraryView: t('basket.itineraryView'),
        total: t('basketDrawer.total'),
        viewBasket: t('basketDrawer.viewBasket'),
        checkout: t('basketDrawer.checkout'),
        continueShopping: t('basket.continueShopping'),
      }}
    />
  );
};
